import { rd } from '@passionware/monads';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getCustomFieldDefinitions } from 'store/v1/custom_fields/custom_fields.selectors';
import { generateCalloutFieldMapper } from 'v1/helpers/calloutFieldHelper';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { MergeServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';

/**
 * Callout field mapper with workspace-merged resource type fields (layer 3).
 */
export function useCalloutFieldMappings(
  resourceTypeId: number | null | undefined,
  enterpriseWorkspaceId: number | null | undefined,
  i18n: Parameters<typeof generateCalloutFieldMapper>[2],
  services: MergeServices<
    [WithAppScopeService, WithMetastructureMappingService]
  >
) {
  const scopeRd = services.appScopeService.useAppScope();
  const fieldsRequest = rd.tryMap(scopeRd, resolved =>
    resourceTypeId == null
      ? undefined
      : {
          type: 'resource' as const,
          subtypeId: resourceTypeId,
          scope: appScopeUtils.identityFromEntityWorkspace(
            resolved.organizationId,
            enterpriseWorkspaceId
          )
        }
  );
  const fieldsRd =
    services.metastructureMappingService.useMetastructure(fieldsRequest);
  const fields = rd.getOrElse(rd.useLastWithPlaceholder(fieldsRd), []);
  const customFieldDefinitions =
    (useSelector(getCustomFieldDefinitions) as unknown[]) ?? [];

  return useMemo(
    () => generateCalloutFieldMapper(fields, customFieldDefinitions, i18n),
    [fields, customFieldDefinitions, i18n]
  );
}
